
import { useReferentBranding } from '@/hooks/useReferentBranding';

interface SuccessStateProps {
  campaignId?: string;
}

export const SuccessState = ({ campaignId }: SuccessStateProps) => {
  const { branding } = useReferentBranding(campaignId);
  
  // Helper pour vérifier et utiliser le branding
  const getBrandedValue = (value: string | undefined | null, defaultValue: string) => {
    return (branding?.enabled && value) ? value : defaultValue;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8 px-4 font-['Bricolage_Grotesque']">
      <div className="w-full max-w-[646px] bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-neutral-400/20 inline-flex flex-col justify-start items-start overflow-hidden">
        {/* Header avec logo et badge */}
        <div className="self-stretch px-4 sm:px-7 py-6 border-b border-neutral-400/20 flex flex-col sm:flex-row justify-between items-center gap-4">
          <img 
            className="w-32 sm:w-40 h-auto" 
            src={getBrandedValue(branding?.logoUrl, "/lovable-uploads/c941c7a1-d271-4c8b-91a4-2611677da8a6.png")}
            alt={getBrandedValue(branding?.logoUrl, null) ? "Logo Entreprise" : "Getproof Logo"}
          />
          <div 
            className="px-2 py-1.5 rounded-md outline outline-1 outline-offset-[-1px] flex justify-center items-center gap-2.5"
            style={{
              outlineColor: getBrandedValue(branding?.primaryColor, '#2dd4bf'),
              color: getBrandedValue(branding?.primaryColor, '#2dd4bf')
            }}
          >
            <div className="justify-start text-sm font-medium font-['Bricolage_Grotesque'] capitalize tracking-tight">
              Prise de référence professionnelle
            </div>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="self-stretch px-7 py-8 flex flex-col justify-end items-center gap-7">
          {/* Section principale avec icône et message */}
          <div className="self-stretch flex flex-col justify-start items-center gap-6">
            {/* Icône de succès */}
            <div 
              className="size-14 p-2.5 rounded-full flex flex-col justify-center items-center gap-2.5 overflow-hidden"
              style={{
                backgroundColor: `${getBrandedValue(branding?.primaryColor, '#2dd4bf')}1A`
              }}
            >
              <div className="relative">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path 
                    d="M4.8125 15.3125L10.9375 21.4375L23.1875 8.3125" 
                    stroke={getBrandedValue(branding?.primaryColor, '#2dd4bf')} 
                    strokeWidth="2.8" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>

            {/* Titre et message */}
            <div className="self-stretch flex flex-col justify-center items-center gap-2">
              <div className="text-center justify-start text-gray-800 text-2xl font-medium font-['Bricolage_Grotesque'] leading-7">
                Merci!
              </div>
              <div className="w-full max-w-[456px] text-center justify-start text-neutral-400 text-base font-normal font-['Bricolage_Grotesque'] leading-tight">
                Vos référents ont été contactés. Vous serez notifié dès que leurs évaluations seront complétées.
              </div>
            </div>
          </div>

          {/* Section informative */}
          <div 
            className="w-full max-w-[590px] p-3 bg-gray-50 border-l-2 flex flex-col justify-center items-center gap-3"
            style={{
              borderLeftColor: getBrandedValue(branding?.primaryColor, '#2dd4bf')
            }}
          >
            <div className="self-stretch justify-start text-gray-800 text-base font-medium font-['Bricolage_Grotesque']">
              Que se passe-t-il ensuite ?
            </div>
            <div className="self-stretch flex flex-col justify-start items-start gap-2">
              <div className="self-stretch inline-flex justify-start items-center gap-2">
                <div className="relative">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path 
                      d="M3.4375 10.9375L7.8125 15.3125L16.5625 5.9375" 
                      stroke={getBrandedValue(branding?.primaryColor, '#2dd4bf')} 
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="justify-start text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque']">
                  Chaque référent reçoit une demande de référence personnalisée.
                </div>
              </div>
              <div className="self-stretch inline-flex justify-start items-center gap-2">
                <div className="relative">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path 
                      d="M3.4375 10.9375L7.8125 15.3125L16.5625 5.9375" 
                      stroke={getBrandedValue(branding?.primaryColor, '#2dd4bf')} 
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="justify-start text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque']">
                  Ils évaluent vos compétences et partagent un feedback écrit ou audio.
                </div>
              </div>
              <div className="self-stretch inline-flex justify-start items-center gap-2">
                <div className="relative">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path 
                      d="M3.4375 10.9375L7.8125 15.3125L16.5625 5.9375" 
                      stroke={getBrandedValue(branding?.primaryColor, '#2dd4bf')} 
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="justify-start text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque']">
                  Vous serez informé dès que toutes les réponses seront reçues et analysées.
                </div>
              </div>
            </div>
          </div>

          {/* Séparateur */}
          <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-neutral-400/20"></div>

          {/* Footer */}
          <div className="flex flex-col justify-center items-center gap-4">
            <div className="w-full max-w-96 text-center justify-center text-neutral-400 text-xs font-normal font-['Bricolage_Grotesque'] leading-none">
              Cette demande de référence est effectuée conformément à la législation en vigueur. Vos réponses sont confidentielles et traitées conformément au RGPD.
            </div>
            <div 
              className="text-center justify-center text-sm font-normal font-['Bricolage_Grotesque'] leading-tight"
              style={{ color: getBrandedValue(branding?.primaryColor, '#2dd4bf') }}
            >
              L'équipe Getproof
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
